// src/components/ProcurementPanel.tsx — v4 (BOM-AUTO-01, Nov 3, 2026): "Mark delivered" on an approved/confirmed materials order
//   (marks its shopping-list lines delivered; when every line for the job is in, the contractor gets the "materials ready" text).
// v3 (PROC-03, Nov 3, 2026): edit lines before approval (an edited sent order becomes the
//   next revision), send / re-send (max 4 sends), status timeline, and the per-PO single-use payment card (owner; last 4 + Stripe link).
// v2 (IDC-01, Nov 3, 2026): print/mail orders (order_kind 'mail') show pieces, route
//   and mail-by date instead of delivery; wording says "the supplier" instead of Mutual.
// PROC-01 M6: one supplier order, its quote and every check,
// with Approve (owner) / Request changes / Reject. All decisions go through approve-procurement-order;
// the console never writes procurement tables itself.
import { useEffect, useState } from "react";
import { canApply, ops, type ProcCheck, type ProcOrder } from "../lib/ops";
import { CatalogPicker } from "./ProcurementEdit";
import { dateTime } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Loading, Notice } from "./ui";
import { Panel } from "./Panel";

const usd = (n: number | null | undefined) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const day = (d: string | null | undefined) => (d ? new Date(d + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "—");

function holdLeft(iso: string | null | undefined): string {
  if (!iso) return "no quote date";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return `expired ${dateTime(iso)}`;
  const h = Math.floor(ms / 3_600_000);
  return h >= 48 ? `${Math.floor(h / 24)} days left` : `${h} h left`;
}

/** A suggested message for the supplier from the blocking checks (the owner edits it before sending). */
function suggestion(checks: ProcCheck[]): string {
  const asks: string[] = [];
  for (const c of checks) {
    if (c.ok || c.severity === "info") continue;
    if (c.id === "tax") asks.push("Please remove the sales tax: this purchase is for resale under our WA reseller permit.");
    if (c.id === "hold") asks.push("The inventory hold on this quote has expired. Please re-issue it with a new hold.");
    if (c.id === "po_match") asks.push("Please put our PO number in the Purchase Order field.");
    if (c.id === "items") asks.push(c.detail);
    if (c.id === "account") asks.push("Please put this quote on our account.");
  }
  return asks.join("\n");
}

export function ProcurementPanel(props: { orderId: string; title: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const [o, setO] = useState<ProcOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [mode, setMode] = useState<"none" | "changes" | "reject">("none");
  const [note, setNote] = useState("");

  const [reload, setReload] = useState(0);
  useEffect(() => {
    let live = true;
    ops.procurementOrder(props.orderId).then((d) => live && setO(d)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [props.orderId, reload]);
  const refresh = () => setReload((n) => n + 1);

  // PROC-03: editing, sending, card
  const EDITABLE = ["draft", "rfq_sent", "quote_received", "needs_review", "awaiting_approval"];
  const [editing, setEditing] = useState(false);
  const [qtys, setQtys] = useState<Record<number, string>>({});
  async function step(label: string, fn: () => Promise<unknown>, done: string) {
    setBusy(label); setError(null);
    try { await fn(); toast(done); refresh(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  const q = o?.quote ?? null;
  const checks = (q?.checks?.results ?? []) as ProcCheck[];
  const blocking = checks.filter((c) => !c.ok && c.severity === "block");
  const flags = checks.filter((c) => c.severity === "flag");
  const isOwner = role === "owner";
  const mayAct = canApply(role);

  async function run(action: "approve" | "reject" | "request_changes") {
    setBusy(action); setError(null);
    try {
      const r = await ops.procurementAct(props.orderId, action, note.trim() || undefined, action === "approve" ? ack : undefined);
      toast(action === "approve" ? `✓ Approved — sent to ${r.sent_to}${r.test_mode ? " (TEST)" : ""}`
        : action === "request_changes" ? `✓ Sent to ${r.sent_to}${r.test_mode ? " (TEST)" : ""}` : "✓ Rejected");
      if (action === "approve" && !r.test_mode) {   // PROC-03: single-use card for this PO (quote total + 10%)
        try { const c = await ops.createPoCard(props.orderId); if (c.ok && c.last4) toast(`✓ Payment card …${c.last4} made for this PO`); }
        catch (e) { toast(`Card not made: ${(e as Error).message}. Use "Create payment card" in the order.`); }
      }
      await props.onDone();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function openPdf() {
    try { const r = await ops.procurementAct(props.orderId, "pdf_url"); if (r.url) window.open(r.url, "_blank", "noopener"); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <Panel kicker="Supplier order" title={props.title} onClose={props.onClose}>
      {!o && !error && <Loading what="the order" />}
      {error && <Notice kind="error">{error}</Notice>}
      {o && (
        <>
          {o.test_mode && <Notice kind="info">TEST MODE — every email from this order goes only to the test address, never to {o.supplier}.</Notice>}
          <dl className="facts">
            <dt>PO</dt><dd className="mono">{o.po_number}</dd>
            <dt>Supplier</dt><dd>{o.supplier} · {o.location?.name}</dd>
            <dt>Quote</dt><dd>{q ? <>{q.supplier_doc_no ?? "—"} · received {dateTime(q.received_at)} {q.has_pdf && <button type="button" className="btn quiet" onClick={openPdf}>Open PDF</button>}</> : "No quote yet"}</dd>
            {o.order_kind === "mail" && o.mail && (<>
              <dt>Print job</dt><dd>{o.mail.pieces} pieces · {o.mail.route ? `EDDM ZIP ${o.mail.zip} route ${o.mail.route}` : `addressed, ZIP ${o.mail.zip ?? "—"}`}{o.mail.campaign_id ? ` · ${o.mail.campaign_id}` : ""}</dd>
              <dt>Mail by</dt><dd>{day(o.mail.mail_by)}</dd>
            </>)}
            {o.order_kind !== "mail" && <dt>{o.fulfillment === "will_call" ? "Will call" : "Delivery"}</dt>}
            {o.order_kind !== "mail" && <dd>{o.fulfillment === "will_call"
              ? <>Pickup {day(o.pickup_on)}{o.pickup_window ? ` · ${o.pickup_window}` : ""} · {o.pickup_contact ?? "—"}</>
              : <>{String(o.ship_to?.address ?? "")} · {o.delivery_latest && o.delivery_latest !== o.deliver_on ? `between ${day(o.deliver_on)} and ${day(o.delivery_latest)}` : `on ${day(o.deliver_on)}`}{o.delivery_window ? ` · ${o.delivery_window}` : ""}</>}</dd>}
            {o.confirmed_delivery_on && (<><dt>Confirmed</dt><dd>{day(o.confirmed_delivery_on)}{o.confirmed_delivery_window ? ` · ${o.confirmed_delivery_window}` : ""}</dd></>)}
            {o.fulfillment !== "will_call" && o.delivery_instructions && (<><dt>{o.order_kind === "mail" ? "Notes" : "Instructions"}</dt><dd>{o.delivery_instructions}</dd></>)}
            <dt>Hold</dt><dd>{holdLeft(q?.hold_expires_at)}</dd>
            <dt>Tax</dt><dd>{o.tax_exempt_today ? "Resale (exempt)" : "Taxable"}{q ? ` · quote charges ${usd(q.totals?.tax)}` : ""}</dd>
          </dl>

          <Timeline o={o} />

          {/* PROC-03: what we asked for — editable until approval */}
          <div className="table-scroll">
            <table className="proc-lines">
              <thead><tr><th>Requested{(o.revision ?? 1) > 1 ? ` · rev ${o.revision}` : ""}</th><th className="num">Qty</th>{editing && <th />}</tr></thead>
              <tbody>
                {o.requested_lines.map((l, i) => (
                  <tr key={`${l.sku}-${i}`}>
                    <td><span className="mono">{l.sku}</span><br /><span className="muted">{l.description}</span></td>
                    <td className="num">{editing
                      ? <input type="number" min={1} step={1} style={{ width: 80 }} value={qtys[i] ?? String(l.qty)} onChange={(e) => setQtys({ ...qtys, [i]: e.target.value })} aria-label={`Quantity for ${l.sku}`} />
                      : <>{l.qty} {l.unit?.toLowerCase() ?? ""}</>}</td>
                    {editing && (
                      <td className="num" style={{ whiteSpace: "nowrap" }}>
                        <button type="button" className="btn quiet" disabled={!!busy || !qtys[i] || Number(qtys[i]) === Number(l.qty) || !(Number(qtys[i]) >= 1)}
                          onClick={() => void step("qty", () => ops.poEditLine(o.order_id, i, l.sku, Math.round(Number(qtys[i]))), `✓ ${l.sku} → ${qtys[i]}`).then(() => setQtys({}))}>Save</button>
                        {o.order_kind !== "mail" && o.requested_lines.length > 1 && (
                          <button type="button" className="btn quiet" disabled={!!busy}
                            onClick={() => void step("remove", () => ops.poRemoveLine(o.order_id, i, l.sku), `✓ Removed ${l.sku}`)}>Remove</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editing && o.order_kind !== "mail" && o.supplier_id && (
            <CatalogPicker supplierId={o.supplier_id} disabled={!!busy}
              onAdd={(materialId, qty, label) => step("add", () => ops.poAddLine(o.order_id, materialId, qty), `✓ Added ${label}`)} />
          )}
          {mayAct && EDITABLE.includes(o.status) && (
            <div className="proc-actions">
              <button type="button" className="btn" disabled={!!busy} onClick={() => { setEditing(!editing); setQtys({}); }}>{editing ? "Done editing" : "Edit order…"}</button>
              {o.status === "draft" && (
                <button type="button" className="btn primary" disabled={!!busy || (o.send_count ?? 0) >= 4}
                  onClick={() => void step("send", () => ops.sendRfq(o.order_id), (o.revision ?? 1) > 1 && (o.send_count ?? 0) > 0 ? `✓ Revised request (rev ${o.revision}) sent` : "✓ Quote request sent")}>
                  {(o.revision ?? 1) > 1 && (o.send_count ?? 0) > 0 ? `Send revised request (rev ${o.revision})` : "Send quote request"}</button>
              )}
              {o.status === "rfq_sent" && (
                <button type="button" className="btn" disabled={!!busy || (o.send_count ?? 0) >= 4}
                  onClick={() => void step("resend", () => ops.resendRfq(o.order_id), "✓ Quote request re-sent")}>
                  Re-send{(o.send_count ?? 0) >= 4 ? " (limit reached)" : ` (${4 - (o.send_count ?? 0)} left)`}</button>
              )}
              <span className="hint" style={{ margin: 0 }}>Sent {o.send_count ?? 0} of 4{o.last_sent_at ? ` · last ${dateTime(o.last_sent_at)}` : ""}</span>
            </div>
          )}
          {editing && (o.status !== "draft" || o.status_reason !== "revised") && (o.send_count ?? 0) > 0 && (
            <p className="hint">Saving an edit makes this order revision {(o.revision ?? 1) + 1}: the current quote is retired and you send the supplier a revised request.</p>
          )}

          <CardPanel o={o} isOwner={isOwner} busy={busy} step={step} />

          {mayAct && o.order_kind !== "mail" && (o.status === "approved" || o.status === "confirmed") && (
            <div className="proc-actions">
              <button type="button" className="btn" disabled={!!busy}
                onClick={() => { if (window.confirm(`Mark ${o.po_number} delivered? When every order for this job is delivered, the contractor gets a "materials ready" text.`))
                  void step("delivered", () => ops.poMarkDelivered(o.order_id), "✓ Marked delivered"); }}>Mark delivered</button>
            </div>
          )}

          {q && (
            <div className="table-scroll">
              <table className="proc-lines">
                <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Net</th></tr></thead>
                <tbody>
                  {q.lines.map((l, i) => (
                    <tr key={i} className={l.kind !== "product" ? "charge" : ""}>
                      <td><span className="mono">{l.sku}</span><br /><span className="muted">{l.description}</span></td>
                      <td className="num">{l.qty} {l.unit?.toLowerCase()}</td>
                      <td className="num mono">{l.unit_price}</td>
                      <td className="num mono">{usd(l.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3}>Subtotal</td><td className="num mono">{usd(q.totals?.subtotal)}</td></tr>
                  <tr><td colSpan={3}>Charges</td><td className="num mono">{usd(q.totals?.charges)}</td></tr>
                  <tr><td colSpan={3}>Tax</td><td className="num mono">{usd(q.totals?.tax)}</td></tr>
                  <tr className="total"><td colSpan={3}>Total</td><td className="num mono">{usd(q.totals?.total)}</td></tr>
                </tfoot>
              </table>
            </div>
          )}

          {checks.length > 0 && (
            <ul className="proc-checks">
              {[...blocking, ...flags, ...checks.filter((c) => c.severity === "info")].map((c) => (
                <li key={c.id} className={!c.ok && c.severity === "block" ? "block" : c.severity === "flag" ? "flag" : "pass"}>
                  <span className="proc-mark" aria-hidden>{!c.ok && c.severity === "block" ? "✕" : c.severity === "flag" ? "!" : "✓"}</span>
                  <span>{c.detail}</span>
                </li>
              ))}
            </ul>
          )}

          {!mayAct && <Notice kind="info">Viewers can see supplier orders but not act on them.</Notice>}
          {mayAct && q && mode === "none" && (
            <div className="proc-actions">
              {isOwner && (
                <>
                  {o.status === "needs_review" && blocking.length === 0 && (
                    <label className="proc-ack"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I've read the flags above</label>
                  )}
                  <button type="button" className="btn primary wide" disabled={!!busy || blocking.length > 0 || (o.status === "needs_review" && !ack)} onClick={() => run("approve")}>
                    {busy === "approve" ? "Sending approval…" : blocking.length ? `Approve (blocked by ${blocking.length})` : "Approve and send"}
                  </button>
                </>
              )}
              <button type="button" className="btn wide" disabled={!!busy} onClick={() => { setMode("changes"); setNote(suggestion(checks)); }}>Request changes…</button>
              <button type="button" className="btn quiet" disabled={!!busy} onClick={() => { setMode("reject"); setNote(""); }}>Reject…</button>
            </div>
          )}
          {mayAct && mode !== "none" && (
            <>
              <textarea className="reason" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={mode === "changes" ? "What should the supplier change? This text is emailed to them in the quote's thread." : "Why reject? (not emailed)"} aria-label="Note" />
              <div className="proc-actions">
                <button type="button" className="btn primary wide" disabled={!!busy || note.trim().length < 5} onClick={() => run(mode === "changes" ? "request_changes" : "reject")}>
                  {busy ? "Working…" : mode === "changes" ? "Send to supplier" : "Reject order"}
                </button>
                <button type="button" className="btn quiet" disabled={!!busy} onClick={() => setMode("none")}>Cancel</button>
              </div>
            </>
          )}

          {(o.edits ?? []).length > 0 && (
            <details className="proc-history"><summary>Edits ({o.edits!.length})</summary>
              <ol>{o.edits!.map((e, i) => <li key={i}>{dateTime(e.at)} · rev {e.revision} · {e.action === "edit_qty" ? `${e.before?.sku}: ${e.before?.qty} → ${e.after?.qty}` : e.action === "add_line" ? `added ${e.after?.sku} × ${e.after?.qty}` : `removed ${e.before?.sku}`} · {e.by}{e.note ? ` · “${e.note}”` : ""}</li>)}</ol>
            </details>
          )}
          {o.history.length > 0 && (
            <details className="proc-history"><summary>Email history ({o.history.length})</summary>
              <ol>{o.history.map((h, i) => <li key={i}>{dateTime(h.at)} · {h.direction === "out" ? "→" : "←"} {h.doc_type} · {h.status} · {h.subject}</li>)}</ol>
            </details>
          )}
        </>
      )}
    </Panel>
  );
}

// ── PROC-03: status timeline ──
const STEPS: Array<{ id: string; label: string }> = [
  { id: "draft", label: "Built" }, { id: "rfq_sent", label: "Quote requested" }, { id: "quote", label: "Quote received" },
  { id: "approved", label: "Approved" }, { id: "confirmed", label: "Confirmed" }, { id: "delivered", label: "Delivered" }, { id: "closed", label: "Closed" },
];
function Timeline({ o }: { o: ProcOrder }) {
  const rank: Record<string, number> = { draft: 0, rfq_sent: 1, quote_received: 2, needs_review: 2, awaiting_approval: 2, approved: 3, confirmed: 4, delivered: 5, closed: 6 };
  const ended = ["cancelled", "rejected", "expired"].includes(o.status);
  const at = rank[o.status] ?? -1;
  const when = (id: string) => {
    const h = o.history.find((x) => (id === "rfq_sent" && x.doc_type === "rfq" && x.direction === "out") || (id === "quote" && x.doc_type === "quote" && x.direction === "in")
      || (id === "approved" && x.doc_type === "approval") || (id === "confirmed" && x.doc_type === "confirmation"));
    return h ? dateTime(h.at) : null;
  };
  return (
    <ol className="proc-timeline" aria-label="Order steps" style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", padding: 0, margin: "8px 0" }}>
      {STEPS.map((s, i) => (
        <li key={s.id} className={i < at ? "done" : i === at ? "now" : "todo"} title={when(s.id) ?? undefined}
          style={{ padding: "2px 8px", borderRadius: 12, border: "1px solid currentColor", opacity: i <= at ? 1 : 0.45, fontWeight: i === at ? 600 : 400 }}>
          {i < at ? "✓ " : ""}{s.label}{i === at && o.status === "draft" && o.status_reason === "revised" ? ` (rev ${o.revision})` : ""}
        </li>
      ))}
      {ended && <li style={{ padding: "2px 8px", fontWeight: 600 }}>✕ {o.status}</li>}
    </ol>
  );
}

// ── PROC-03: per-PO single-use payment card ──
function CardPanel({ o, isOwner, busy, step }: { o: ProcOrder; isOwner: boolean; busy: string | null; step: (l: string, fn: () => Promise<unknown>, done: string) => Promise<void> }) {
  const c = o.card ?? null;
  const approved = o.status === "approved" || o.status === "confirmed";
  if (!c && !(approved && !o.test_mode)) return null;
  return (
    <div className="editor-section card">
      <p className="action-title">Payment card</p>
      {c ? (
        <>
          <p>
            Card <span className="mono">…{c.last4 ?? "????"}</span> · limit {usd((c.limit_cents ?? 0) / 100)} · <strong>{c.status}</strong>
            {c.expires_at && c.status === "active" ? ` · good until ${dateTime(c.expires_at)}` : ""}
          </p>
          <p className="hint">Single use: Stripe cancels it after one payment, and we refuse it after 30 days or once the order closes. Read the full number in Stripe for a phone order.</p>
          <div className="row-actions">
            <a className="btn" href={`https://dashboard.stripe.com/issuing/cards/${c.id}`} target="_blank" rel="noopener noreferrer">Open in Stripe</a>
            {isOwner && c.status === "active" && (
              <button type="button" className="btn quiet" disabled={!!busy} onClick={() => void step("cancel_card", () => ops.cancelPoCard(o.order_id), "✓ Card canceled")}>Cancel card</button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="hint">No card yet for this approved order. It is made for the approved quote total + 10%.</p>
          {isOwner && o.payment_status === "unpaid" && (
            <button type="button" className="btn primary" disabled={!!busy}
              onClick={() => void step("card", async () => { const r = await ops.createPoCard(o.order_id); if (!r.ok) throw new Error("card not made"); }, "✓ Payment card made")}>Create payment card</button>
          )}
        </>
      )}
    </div>
  );
}
